//
// Copyright (c) oos, Inc. and contributors. All rights reserved.
// Licensed under the MIT license. See LICENSE file in the project root for more details.
//

import { RefObject, useContext, useEffect, useMemo, useRef } from 'react'
import Analytics, { AnalyticsContext, PlayerConfig } from '../analytics'
import { ReportMetrics } from '../analytics'

export type AnalyticOptions = {
  send?: (metrics: ReportMetrics) => Promise<void> | void
  maxAttempts?: number
  onRequeue?: (metrics: ReportMetrics) => Promise<void> | void
  onQueue?: (metrics: ReportMetrics) => Promise<void> | void
  onComplete?: (metrics: ReportMetrics) => Promise<void> | void
  onError?: (metrics: ReportMetrics) => Promise<void> | void
  onFail?: (metrics: ReportMetrics) => Promise<void> | void
  playerConfig?: PlayerConfig
  timeInterval?: number
  videoId?: string | null
  playerVersion?: string | null
}

export function useAnalytics<PlayerElement>(
  player: RefObject<PlayerElement> | PlayerElement,
  options?: AnalyticOptions,
  deps: unknown[] = [],
) {
  const { getVideoElement, setDefaultPlayerConfig, defaultPlayerConfig, defaultTimeInterval, viewerIdKey } =
    useContext(AnalyticsContext)

  const analyticsRef = useRef<Analytics | null>(null)
  const sendRef = useRef(options?.send)

  // Memoize the send function
  const memoizedSend = useMemo(() => options?.send, []) // Intentionally empty deps

  // Memoize the other options
  const memoizedOptions = useMemo(
    () => ({
      ...options,
      send: memoizedSend, // Use memoized send function
      timeInterval: options?.timeInterval || defaultTimeInterval,
    }),
    [options?.videoId, options?.timeInterval, defaultTimeInterval, memoizedSend, ...deps],
  )

  // Only create the Analytics instance once
  if (!analyticsRef.current) {
    analyticsRef.current = new Analytics(memoizedOptions)
  }

  // Update send function reference
  useEffect(() => {
    sendRef.current = options?.send
  })

  // Update options only when memoizedOptions changes
  useEffect(() => {
    analyticsRef.current?.updateOptions({
      ...memoizedOptions,
      // Always use latest send function from ref
      send: (...args) => sendRef.current?.(...args),
    })
  }, [memoizedOptions])

  const currentPlayer = useMemo(
    () => (player && typeof player === 'object' ? ('current' in player ? player.current : player) : undefined),
    [player],
  )

  useEffect(() => {
    let isAttached = false

    if (currentPlayer) {
      getVideoElement(currentPlayer, options?.playerConfig).then((video) => {
        if (video && !isAttached && analyticsRef.current) {
          isAttached = true
          analyticsRef.current.attach({
            video,
            viewerIdKey,
          })
        }
      })
    }

    return () => {
      if (isAttached && analyticsRef.current) {
        analyticsRef.current.detach()
      }
    }
  }, [currentPlayer, viewerIdKey, options?.videoId])

  return {
    analytics: analyticsRef.current,
    setDefaultPlayerConfig,
    defaultPlayerConfig,
  }
}
